import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ResidenceSection } from '../ResidenceSection';
import { Apartment } from '../../../../graphql/graphql';
import {
  ApartmentsInfoContainer,
  DeveloperContainer,
  DeveloperImage,
  DeveloperTitle,
  BuildingInfo,
  BuildingInfoItem,
  ResidenceDot
} from './styled';
import { ApartmentInfo } from '../../ApartmentInfo';
import { prettyNumber } from '../../../../utils/helpers';

interface MainInfoProps {
  companyName?: string;
  logoUrl?: string;
  deadline?: string | null;
  priceMin?: number | null;
  currency?: string;
  apartments?: Apartment[] | null;
  hasBillOfSale?: boolean;
  buildingsCount?: number | null;
  floorsPerBuilding?: string | null;
  apartmentsCount?: number | null;
}

export const MainInfo: React.FC<MainInfoProps> = ({
  companyName,
  logoUrl,
  deadline,
  priceMin,
  currency,
  apartments,
  hasBillOfSale,
  buildingsCount,
  floorsPerBuilding,
  apartmentsCount
}) => {
  const { t } = useTranslation();

  const buildingsInfo = useMemo(
    () =>
      [
        {
          id: 'buildings',
          label: t('residences.buildings'),
          value: buildingsCount,
          isHidden: !buildingsCount
        },
        {
          id: 'floors',
          label: t('residences.floors'),
          value: floorsPerBuilding,
          isHidden: !floorsPerBuilding
        },
        {
          id: 'flats',
          label: t('residences.flats'),
          value: prettyNumber(apartmentsCount),
          isHidden: !apartmentsCount
        }
      ].filter(item => !item.isHidden),
    [buildingsCount, floorsPerBuilding, apartmentsCount, t]
  );

  return (
    <ResidenceSection>
      <DeveloperContainer>
        {logoUrl && (
          <DeveloperImage
            src={logoUrl}
            alt={t('residences.logo', { name: companyName })}
            width={60}
            height={60}
          />
        )}
        {companyName && (
          <DeveloperTitle tag="title_4_new" data-cy="developer-title">
            {t('residences.title', { name: companyName })}
          </DeveloperTitle>
        )}
      </DeveloperContainer>

      <BuildingInfo>
        {buildingsInfo.map((item, index) => (
          <BuildingInfoItem key={item.id} tag="body_2">
            {item.label}: {item.value}
            {index < buildingsInfo.length - 1 && <ResidenceDot>•</ResidenceDot>}
          </BuildingInfoItem>
        ))}
      </BuildingInfo>

      <ApartmentsInfoContainer>
        <ApartmentInfo
          deadline={deadline}
          priceMin={priceMin}
          currency={currency}
          apartments={apartments}
          hasBillOfSale={hasBillOfSale}
        />
      </ApartmentsInfoContainer>
    </ResidenceSection>
  );
};
